import { targetAudienceServiceClient } from 'services/backofficeIntegration/http/client/createTargetAudienceServiceClient';
import { queryEndpoint } from 'services/backofficeIntegration/http/endpointDefinition';
import { PaginatedListParams } from 'services/backofficeIntegration/http/endpoints/aiWriter/httpGetProjects';
import { targetAudiencesQueryKeys } from 'services/backofficeIntegration/http/queryKeyFactories/targetAudiencesQueryKeys';
import { queryClient } from 'utils/ReactQueryClient';

export type GetTargetAudiencesSuccessDto = TargetAudienceTargetAudiencePage;

export const httpGetTargetAudiences = queryEndpoint({
  makeQueryKey: (params: GetTargetAudiencesParams) => {
    return [...targetAudiencesQueryKeys.forList(), params] as const;
  },
  callEndpoint: ({ workspaceId, body }: GetTargetAudiencesParams) => {
    return targetAudienceServiceClient
      .get<GetTargetAudiencesSuccessDto>(`workspaces/${workspaceId}/target-audiences`, {
        params: body
      })
      .then(response => response);
  }
});

export function invalidateTargetAudiencesQuery() {
  return queryClient.invalidateQueries({
    queryKey: [...targetAudiencesQueryKeys.forList()]
  });
}

export const invalidateTargetAudienceQuery = (targetAudienceId: string) =>
  queryClient.invalidateQueries({
    queryKey: [...targetAudiencesQueryKeys.forTargetAudienceById(targetAudienceId)]
  });

export type GetTargetAudiencesParams = PaginatedListParams & {
  workspaceId: string;
  body?: {
    /**
     * page number
     */
    page?: number;
    /**
     * page size
     */
    size?: number;
    /**
     * find audience by name
     */
    search?: string;
    sort?: string;
    nativeLanguage?: string;
    country?: string;
  };
};

type NfpagePage = {
  currentPage?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
};

type TargetAudienceTargetAudiencePage = {
  data?: Array<ModelTargetAudience>;
  page?: NfpagePage;
};

export type ModelTargetAudience = {
  _embedded?: ModelTargetAudienceEmbedded;
  country: string;
  createdAt?: string;
  /**
   * not set for workspaces imported from legacy team
   */
  createdByUserId?: string;
  genderDistribution?: ModelGenderDistribution;
  id?: string;
  /**
   * A list of strings stored in the database as JSON
   */
  interests: Array<string>;
  maxAge: number;
  /**
   * We store age range as separate min/max fields in the database
   */
  minAge: number;
  name: string;
  nativeLanguage: string;
  /**
   * A list of strings stored in the database as JSON
   */
  needs: Array<string>;
  ownerCustomerId?: number;
  /**
   * A list of strings stored in the database as JSON
   */
  painPoints: Array<string>;
  residence: string;
  shared?: boolean;
  updatedAt?: string;
  workspaceId?: string;
};

type ModelGenderDistribution = {
  female?: number;
  male?: number;
  nonBinary?: number;
  preferNotToSay?: number;
};

type ModelTargetAudienceEmbedded = {
  image?: ModelTargetAudienceImage;
  permissions?: ModelPermission;
};

type ModelTargetAudienceImage = {
  createdAt?: string;
  /**
   * not set for workspaces imported from legacy team
   */
  createdByUserId?: string;
  id?: string;
  targetAudienceId?: string;
  updatedAt?: string;
  url?: string;
};

type ModelPermission = {
  canDelete?: boolean;
  canDuplicate?: boolean;
  canUpdate?: boolean;
  canView?: boolean;
};
